/**
 * Serviço de Preparação de Contexto de Exportação
 * 
 * Prepara todos os dados necessários para executar uma exportação,
 * incluindo diretório, formato, SMB e payload do request.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { normalizeWindowsPath } from '../../../utils/path';
import { sendToTerminal, logger } from '../../../lib/terminalLogger';
import { COREL_ACTIONS } from '../../../lib/corel/constants/actions';
import { buildOverridesForFormat } from '../services/overrides';
import { generateExportFileName } from '../../../utils/fileNaming';
import { buildIdempotencyKey } from '../utils/idempotency';
import type { ExportFormat } from '../exports/types';

export interface SmbCredentials {
  username: string;
  password: string;
  domain?: string | null;
}

export interface ExportContextData {
  outputDir: string;
  effectiveFormat: ExportFormat;
  targetsWithNames: Array<{
    staticId: string;
    guid: string;
    multiplicador: number;
    contracao?: string;
    output_name: string;
  }>;
  smb?: SmbCredentials;
  overrides: any;
  orcId: string | number;
  oitId: string | number;
  proId: number;
}

/**
 * Constrói o caminho completo de output seguindo padrão:
 * \ANO\MES-Nome\DD.MM.YYYY\{diretorio_nome}\orc_id\oit_id
 */
export function buildOutputDirectory(
  baseDir: string,
  saved: any
): string {
  const date = new Date();
  const ano = date.getFullYear();
  const mesesNomes = [
    '01-JANEIRO', '02-FEVEREIRO', '03-MARÇO', '04-ABRIL',
    '05-MAIO', '06-JUNHO', '07-JULHO', '08-AGOSTO',
    '09-SETEMBRO', '10-OUTUBRO', '11-NOVEMBRO', '12-DEZEMBRO'
  ];
  const mes = mesesNomes[date.getMonth()];
  const dia = date.getDate().toString().padStart(2, '0');
  const dataFormatada = `${dia}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${ano}`;
  const orcId = saved?.pedido?.orc_id || '0';
  const oitId = saved?.pedido?.oit_id || '0';
  const diretorioNome = saved?.regras?.diretorio_nome || saved?.regras?.diretorios?.nome || 'INDEFINIDO';

  const outputDir = normalizeWindowsPath(
    `${baseDir}\\${ano}\\${mes}\\${dataFormatada}\\${diretorioNome}\\${oitId}`
  );

  try {
    sendToTerminal('📂 [EXPORT] outputDir final:', { outputDir, diretorioNome });
  } catch { }

  return outputDir;
}

/**
 * Extrai credenciais SMB das regras de segurança
 */
export function extractSmbCredentials(saved: any): SmbCredentials | undefined {
  try {
    const sec = saved?.regras?.security || saved?.regras?.seguranca || {};
    const smbCreds = sec?.exportprod?.smb || sec?.interno?.smb || sec?.externo?.smb || sec?.smb;

    if (smbCreds?.username && (smbCreds?.password || smbCreds?.password_enc)) {
      // ✅ Validar username
      const username = String(smbCreds.username).trim();
      if (!username || username.length === 0) return undefined;

      // ✅ Validar password (prioriza password, depois password_enc)
      const passwordValue = smbCreds?.password || smbCreds?.password_enc;
      const password = String(passwordValue).trim();
      if (!password || password.length === 0) return undefined;

      // ✅ Montar payload SMB
      const smb: SmbCredentials = {
        username: username,
        password: password  // ✅ SEMPRE usar "password" (não password_enc)
      };

      // ✅ Domain: APENAS se for string não-vazia (conforme padrão HMAC)
      const domainValue = smbCreds?.domain;
      if (domainValue !== null && domainValue !== undefined && typeof domainValue === 'string') {
        const domainTrimmed = String(domainValue).trim();
        if (domainTrimmed.length > 0) {
          smb.domain = domainTrimmed;
        }
        // ⚠️ Se domain for null, undefined ou string vazia, NÃO inclui no objeto
      }

      try {
        logger.debug('🔐 [EXPORT] SMB detectado:', {
          user: smb.username,
          hasPwd: !!smb.password,
          domain: smb.domain || '(não incluído)'
        });
        sendToTerminal('🔐 [EXPORT] SMB detectado:', {
          user: smb.username,
          hasPwd: !!smb.password,
          domain: smb.domain || '(não incluído)'
        });
      } catch { }

      return smb;
    }
  } catch { }

  return undefined;
}

/**
 * Enriquece targets com nomes de arquivo finais
 */
export function enrichTargetsWithNames(
  targets: Array<{ staticId: string; guid: string; multiplicador?: number; contracao?: string }>,
  saved: any
): Array<{ staticId: string; guid: string; multiplicador: number; contracao?: string; output_name: string }> {
  const pedidoInfo = {
    orc_id: saved?.pedido?.orc_id,
    oit_id: saved?.pedido?.oit_id,
    material: saved?.material,
    cliente: saved?.cliente,
  };

  const total = targets.length;

  return targets.map((t, idx) => {
    const mult = typeof t.multiplicador === 'number' ? t.multiplicador : 1;
    const outName = generateExportFileName(idx + 1, total, pedidoInfo, mult, t.contracao);

    return {
      staticId: t.staticId,
      guid: t.guid,
      multiplicador: mult,
      contracao: t.contracao,
      output_name: outName,
    };
  });
}

/**
 * Constrói o request payload para CorelDRAW
 * MAPEIA exportMode para action correta:
 * - 'batch' → corel-export-batch (Production)
 * - 'simple' → corel-export-simple (Production/Sales)
 * - 'layout' → exportar-objetos-png-lite (Layout PNG)
 */
export function buildExportRequest(
  exportMode: 'batch' | 'simple',
  context: ExportContextData,
  requestId: string,
  snapshotId: string | undefined,
  idempotencyKey: string
): any {
  const { outputDir, effectiveFormat, targetsWithNames, smb, overrides } = context;

  sendToTerminal('🎯 [EXPORT:BUILD_REQUEST]', {
    exportMode,
    format: effectiveFormat,
    targetsCount: targetsWithNames?.length,
    hasSmb: !!smb,
    overridesCount: Object.keys(overrides || {}).length
  });

  const commonData = {
    profile_id: 1, // Será sobrescrito pelo caller se necessário
    format: effectiveFormat,
    output_dir: normalizeWindowsPath(outputDir),
    ...(snapshotId ? { snapshotId } : {}), // ✅ Só inclui se tiver valor (evita undefined no HMAC)
    snapshotPolicy: 'warn' as const,
    bbox: { mode: 'tight' as const, bleed_mm: 0 },
    overrides,
    idempotencyKey,
    // pro_id: proId, // ❌ REMOVIDO - não está na documentação oficial e causa divergência no HMAC
    ...(smb ? { smb } : {})
  };

  if (exportMode === 'simple') {
    sendToTerminal('📤 [EXPORT:ACTION_SELECTED]', {
      action: COREL_ACTIONS.EXPORT_SIMPLE,
      reason: 'simple mode chosen',
      hasFilenameTemplate: true
    });

    return {
      action: COREL_ACTIONS.EXPORT_SIMPLE,
      requestId,
      data: {
        ...commonData,
        filename_template: '{index:000}_{namearquivo}',
      }
    };
  } else {
    sendToTerminal('📤 [EXPORT:ACTION_SELECTED]', {
      action: COREL_ACTIONS.EXPORT_BATCH,
      reason: 'batch mode chosen',
      itemsCount: targetsWithNames.length
    });

    const requestPayload = {
      action: COREL_ACTIONS.EXPORT_BATCH,
      requestId,
      data: {
        ...commonData,
        targets: targetsWithNames,
      }
    };

    // ✅ LOG: Request construído antes de retornar
    console.log('📤 [EXPORT:BUILD_REQUEST_COMPLETE] Request construído', {
      action: requestPayload.action,
      requestId: requestPayload.requestId,
      format: requestPayload.data.format,
      overridesCount: Object.keys(requestPayload.data.overrides || {}).length
    });
    sendToTerminal('📤 [EXPORT:BUILD_REQUEST_COMPLETE] Request construído', {
      action: requestPayload.action,
      requestId: requestPayload.requestId,
      format: requestPayload.data.format,
      overridesCount: Object.keys(requestPayload.data.overrides || {}).length,
      targetsCount: requestPayload.data.targets?.length || 0
    });

    return requestPayload;
  }
}

/**
 * Prepara todo o contexto necessário para executar uma exportação
 */
export async function prepareExportContext(deps: {
  baseDir: string;
  saved: any;
  targets: Array<{ staticId: string; guid: string; multiplicador?: number; contracao?: string }>;
  effectiveFormat: ExportFormat;
  activeProfile: any;
  payloadOverrides: any;
  exportMode: 'batch' | 'simple';
}): Promise<{
  context: ExportContextData;
  overridesBuildResult: { ok: boolean; value?: any; error?: string };
}> {
  const { baseDir, saved, targets, effectiveFormat, activeProfile, payloadOverrides, exportMode } = deps;

  // 1. Construir diretório de saída
  const outputDir = buildOutputDirectory(baseDir, saved);

  // 2. Extrair credenciais SMB
  const smb = extractSmbCredentials(saved);

  // 3. Build overrides - 100% da API
  const profileOptions = (activeProfile?.options as any)?.[effectiveFormat] || null;

  // Log: dados da API antes de processar
  sendToTerminal('📊 [EXPORT:API_PROFILE_OPTIONS]', {
    format: effectiveFormat,
    profileId: activeProfile?.id,
    profileName: activeProfile?.name,
    hasOptions: !!profileOptions,
    optionsKeys: profileOptions ? Object.keys(profileOptions) : [],
    optionsPreview: profileOptions ? JSON.stringify(profileOptions).substring(0, 200) : null
  });

  const overridesBuild = buildOverridesForFormat(effectiveFormat, profileOptions, payloadOverrides || {});

  if (!overridesBuild.ok) {
    sendToTerminal('❌ [EXPORT:OVERRIDES_BUILD_FAILED]', { error: overridesBuild.error });
    return {
      context: {} as any,
      overridesBuildResult: overridesBuild
    };
  }

  // Log: resultado final dos overrides
  sendToTerminal('✅ [EXPORT:OVERRIDES_BUILT]', {
    format: effectiveFormat,
    fieldsCount: Object.keys(overridesBuild.value).length,
    fields: Object.keys(overridesBuild.value),
    preview: JSON.stringify(overridesBuild.value).substring(0, 300)
  });

  // 4. Enriquecer targets com nomes
  const targetsWithNames = enrichTargetsWithNames(targets, saved);

  // 5. Extrair IDs
  const orcId = saved?.pedido?.orc_id || '0';
  const oitId = saved?.pedido?.oit_id || '0';
  const proId = Number(saved?.produto?.pro_id ?? saved?.produto?.id ?? 0) || 0;

  return {
    context: {
      outputDir,
      effectiveFormat,
      targetsWithNames,
      smb,
      overrides: overridesBuild.value,
      orcId,
      oitId,
      proId
    },
    overridesBuildResult: overridesBuild
  };
}

